// Content intake for USB and disc duplication (ADR-021). The portal API only
// coordinates: it records the manifest, mints a short-lived write SAS scoped to
// the session's prefix, reports which blocks are already staged, and verifies
// the result. The bytes themselves go browser → media store (see
// components/mediaContentUpload.ts).

import type {
  CreateMediaContentSessionRequest,
  CreateMediaContentSessionResponse,
  FinalizeMediaContentSessionResponse,
  MediaContentQuoteStateResponse,
  MediaContentSas,
  MediaContentSessionStateResponse,
  MediaLinkCheckDto,
  MediaLinkCheckRequest,
} from 'shared';

import { api } from './client.js';

export async function getQuoteMediaContentState(
  quote_id: string
): Promise<MediaContentQuoteStateResponse> {
  const res = await api.get<MediaContentQuoteStateResponse>(
    `/media-content/quotes/${encodeURIComponent(quote_id)}/state`
  );
  return res.data;
}

export async function createMediaContentSession(
  body: CreateMediaContentSessionRequest
): Promise<CreateMediaContentSessionResponse> {
  const res = await api.post<CreateMediaContentSessionResponse>('/media-content/sessions', body);
  return res.data;
}

export async function refreshMediaContentSas(session_id: string): Promise<MediaContentSas> {
  const res = await api.post<MediaContentSas>(
    `/media-content/sessions/${encodeURIComponent(session_id)}/sas`,
    {}
  );
  return res.data;
}

export async function getMediaContentSessionState(
  session_id: string
): Promise<MediaContentSessionStateResponse> {
  const res = await api.get<MediaContentSessionStateResponse>(
    `/media-content/sessions/${encodeURIComponent(session_id)}/state`
  );
  return res.data;
}

export type FinalizeMismatch = Extract<FinalizeMediaContentSessionResponse, { ok: false }>;

function isMismatch(body: unknown): body is FinalizeMismatch {
  if (!body || typeof body !== 'object') return false;
  const b = body as Partial<FinalizeMismatch>;
  return b.ok === false && Array.isArray(b.missing) && Array.isArray(b.size_mismatch);
}

// A 409 carrying the bare `{ ok: false, missing, size_mismatch }` body is an
// expected answer (the files that did not arrive intact), so it resolves rather
// than throwing. Any other 409 is an error envelope (the session is no longer
// uploading) and throws like everything else.
export async function finalizeMediaContentSession(
  session_id: string
): Promise<FinalizeMediaContentSessionResponse> {
  try {
    const res = await api.post<FinalizeMediaContentSessionResponse>(
      `/media-content/sessions/${encodeURIComponent(session_id)}/finalize`,
      {}
    );
    return res.data;
  } catch (err) {
    const response = (err as { response?: { status?: number; data?: unknown } }).response;
    if (response?.status === 409 && isMismatch(response.data)) return response.data;
    throw err;
  }
}

export async function cancelMediaContentSession(session_id: string): Promise<void> {
  await api.delete(`/media-content/sessions/${encodeURIComponent(session_id)}`);
}

export async function checkMediaContentLink(
  body: MediaLinkCheckRequest
): Promise<MediaLinkCheckDto> {
  const res = await api.post<MediaLinkCheckDto>('/media-content/link-checks', body);
  return res.data;
}
